import {
  Button as AntdButton,
  message,
  Select,
  Table,
  TableColumnsType,
} from 'antd'
import React, { useCallback, useState } from 'react'
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail'
import CloseIcon from '@mui/icons-material/Close'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import {
  Box,
  Button,
  Dialog,
  Grid,
  IconButton,
  Typography,
} from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import dayjs from 'dayjs'
import { Link, useNavigate } from 'react-router-dom'
import CallIcon from '@mui/icons-material/Call'
import AddIcCallIcon from '@mui/icons-material/AddIcCall'
import { FiCopy } from 'react-icons/fi'

import { useUser } from '../../../../../hooks/useUser'
import {
  EORDER_STATUS_CALL,
  EOrderStatus,
  EOrderType,
  IOrder,
  IOrderItem,
  OrderRecipient,
  OrderShippingProvider,
} from '../../../../../types/order'
import TrackingProviderImage from '../../../../../components/TrackingProviderImage'
import {
  ACTION_TYPE_TITLES,
  ONLY_DATE_FORMAT,
  ONLY_DATE_TIME_FORMAT,
  ONLY_TIME_FORMAT,
} from '../../../../../constants/global'
import CircularLoading from '../../../../../components/CircularProgress'
import PageUrls from '../../../../../constants/page-urls'
import ProductItemDetailTag from '../../../../../components/ProductItemDetailTag'
import OrderTag from '../../../../../components/OrderTag'
import { IFilterMapped } from '../../../components/OrderFilter'
import { axiosInstance } from '../../../../../hooks/useAxios'
import { Tag } from '../../../../../components/Tag'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import { ERRORS_MESSAGE_MAPPER } from '../../../../../constants/errors'

interface IOrderListTeleSaleTableProps {
  selectedData: IOrder[]
  setSelectedData: (orders: IOrder[]) => void
  isLoading: boolean
  data: IOrder[]
  filter: IFilterMapped
  onFilterChange: (filter: IFilterMapped) => void
  total: number
  onUpdateOrderSuccess: () => void
  hasNext?: boolean
  hasPrev?: boolean
  onNext?: () => void
  onPrev?: () => void
  onPerPageChange?: (perPage: number) => void
  pageIndex?: number
}

const PER_PAGE_OPTIONS = [10, 15, 20, 30, 50, 100]

enum OrderStatusColor {
  'pending' = 'warning.main',
  'telesale' = 'secondary.main',
  'salepage' = 'text.secondary',
  'success' = 'success.main',
  'cancelled' = 'error.main',
}

const OrderListTeleSaleTable = (props: IOrderListTeleSaleTableProps) => {
  const { merchant } = useUser()
  const showSocial = !!merchant?.config?.display?.social

  const {
    selectedData,
    setSelectedData,
    isLoading,
    data,
    filter,
    onFilterChange,
    total,
    onUpdateOrderSuccess,
    hasNext = false,
    hasPrev = false,
    onNext,
    onPrev,
    onPerPageChange,
    pageIndex = 1,
  } = props
  const navigate = useNavigate()

  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [order, setOrder] = useState<IOrder | null>(null)
  const [slipUrl, setSlipUrl] = useState<string | null>(null)

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    const keys = newSelectedRowKeys as string[]
    const orderObjects = data?.reduce<{ [key: string]: IOrder }>((acc, cur) => {
      acc[cur.objectId] = cur
      return acc
    }, {})
    const orders = keys.map((key: string) => {
      return orderObjects[key]
    })
    setSelectedData(orders)
  }

  const changeStatusCall = async (
    status: EORDER_STATUS_CALL,
    objectId: string,
  ) => {
    try {
      await axiosInstance.put(`/order/${objectId}/status-call`, {
        status,
      })
      message.success('แก้ไขสถานะสำเร็จ')
      onUpdateOrderSuccess()
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
  }

  const handleShowTracking = useCallback((order: IOrder) => {
    setIsOpen(true)
    setOrder(order)
  }, [])

  const handleCopyText = useCallback((text: string) => {
    navigator.clipboard.writeText(text ?? '')
    message.success(`คัดลอกแล้ว: ${text ?? ''}`)
  }, [])

  const columns: TableColumnsType<IOrder & { key: string; numberRow: number }> =
    [
      {
        title: 'ลำดับ',
        dataIndex: 'numberRow',
        key: 'numberRow',
        align: 'center' as const,
        width: 80,
        render: (numberRow: number) => {
          return (
            <Box>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                textAlign={'center'}
              >
                {numberRow}
              </Typography>
            </Box>
          )
        },
      },
      {
        title: 'สถานะ',
        dataIndex: 'status',
        key: 'status',
        align: 'center' as const,
        render: (status: string, order: IOrder) => {
          const lastTracking =
            order.trackings?.[order.trackings?.length - 1] ??
            ({} as { trackingDate: string })
          return (
            <>
              {order.trackingCode && order.trackingProvider && (
                <Box mb={1}>
                  <TrackingProviderImage
                    trackingProvider={
                      order.trackingProvider as OrderShippingProvider
                    }
                  />
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    gap={0.5}
                  >
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      textAlign={'center'}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleShowTracking(order)}
                    >
                      {order.trackingCode}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleCopyText(order?.trackingCode ?? '')}
                    >
                      <FiCopy size={12} />
                    </IconButton>
                  </Box>
                  <Typography fontSize={10}>
                    {order?.trackingUpdatedAt
                      ? dayjs(order?.trackingUpdatedAt).format(
                          ONLY_DATE_TIME_FORMAT,
                        )
                      : lastTracking?.trackingDate
                        ? dayjs(lastTracking.trackingDate).format(
                            ONLY_DATE_TIME_FORMAT,
                          )
                        : ''}
                  </Typography>
                </Box>
              )}
              <Box>
                <OrderTag order={order} status={status as EOrderStatus} />
              </Box>
              {order.providerStatusText && (
                <Box
                  pt={1}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleShowTracking(order)}
                >
                  <Tag color={`success`}>{order.providerStatusText}</Tag>
                </Box>
              )}
            </>
          )
        },
      },
      {
        title: 'เลขออเดอร์',
        dataIndex: 'code',
        key: 'code',
        align: 'center' as const,
        render: (code: string, order: IOrder) => (
          <>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={0.5}
            >
              <Typography
                variant="subtitle1"
                color="text.primary"
                component={Link}
                to={`${PageUrls.Orders.Detail}/${order.objectId}`}
                sx={{ cursor: 'pointer' }}
              >
                {code}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleCopyText(`${order?.code ?? ''}`)}
              >
                <FiCopy size={12} />
              </IconButton>
            </Box>
            <Typography fontSize={11}>
              {order.orderDate
                ? dayjs(order.orderDate).format(ONLY_DATE_FORMAT)
                : ''}
            </Typography>
          </>
        ),
      },
      {
        title: 'ราคา',
        dataIndex: 'total',
        key: 'total',
        align: 'center' as const,
        render: (total: string, order: IOrder) => (
          <Grid
            container
            flexDirection={'column'}
            textAlign={'center'}
            minWidth={120}
          >
            <Grid item>
              <Typography
                variant="h5"
                color={
                  order?.paymentMethod === EOrderType.COD ? 'red' : 'green'
                }
              >
                {total.toLocaleString()}
              </Typography>
            </Grid>
            <Grid item>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={0.5}
                sx={{ marginTop: 1 }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  {order?.paymentMethod === EOrderType.COD
                    ? 'เก็บเงินปลายทาง'
                    : 'โอนผ่านธนาคาร'}
                </Typography>
                {order?.paymentMethod !== EOrderType.COD &&
                  order?.paymentUrl && (
                    <IconButton
                      size="small"
                      title="ดูสลิป"
                      onClick={() => setSlipUrl(order.paymentUrl ?? null)}
                    >
                      <ReceiptLongIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
              </Box>
            </Grid>
            <Grid item>
              <Typography variant="subtitle2" color="text.secondary">
                {order.items.length} รายการ/{order.quantity} ชิ้น
              </Typography>
            </Grid>
          </Grid>
        ),
      },
      {
        title: 'จัดส่ง',
        dataIndex: 'recipient',
        key: 'recipient',
        align: 'center' as const,
        render: (recipient: OrderRecipient) => (
          <Grid
            container
            flexDirection={'column'}
            minWidth={200}
            textAlign={'left'}
          >
            <Grid item>
              <Typography variant="subtitle1">{recipient?.name}</Typography>
            </Grid>
            <Grid item>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ marginTop: 1 }}
              >
                {recipient?.address} {recipient?.subDistrict}{' '}
                {recipient?.district} {recipient?.province} {recipient?.zipcode}
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="subtitle1">{recipient?.phone}</Typography>
            </Grid>
            {showSocial && recipient?.social && (
              <Grid item>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                  title="โซเชียลของลูกค้า"
                >
                  <AlternateEmailIcon
                    sx={{ fontSize: 14 }}
                    color="disabled"
                  />
                  <Typography variant="subtitle2" color="text.secondary">
                    {recipient.social}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        ),
      },
      {
        title: 'สินค้า',
        dataIndex: 'items',
        key: 'items',
        align: 'center' as const,
        render: (items: IOrderItem[]) => (
          <Box minWidth={150}>
            {items?.map((item: IOrderItem, index: number) => {
              return (
                <ProductItemDetailTag
                  key={index}
                  item={{
                    name: item.productName,
                    quantity: item.quantity,
                    isCollection: item.isCollection,
                    url: item.imageUrl,
                    commissionName: item.commissionUsername,
                    color: item.meta?.color,
                    size: item.meta?.size,
                  }}
                />
              )
            })}
          </Box>
        ),
      },
      {
        title: 'การโทร',
        dataIndex: 'statusCall',
        key: 'statusCall',
        width: 160,
        align: 'center' as const,
        render: (statusCall: EORDER_STATUS_CALL, order: IOrder) => {
          return (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 1,
              }}
            >
              {statusCall === EORDER_STATUS_CALL.NOT_CALLED ? (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={
                      <CallIcon
                        sx={{ transform: 'rotate(-45deg)' }}
                        color={'success'}
                      />
                    }
                    color={'success'}
                    onClick={() =>
                      changeStatusCall(
                        EORDER_STATUS_CALL.CALLED,
                        order?.objectId!,
                      )
                    }
                  >
                    โทรแล้ว
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={
                      <AddIcCallIcon
                        sx={{ transform: 'rotate(-45deg)' }}
                        color={'error'}
                      />
                    }
                    color={'error'}
                    onClick={() =>
                      changeStatusCall(
                        EORDER_STATUS_CALL.CANCELLED,
                        order?.objectId!,
                      )
                    }
                  >
                    ไม่รับสาย
                  </Button>
                </>
              ) : (
                <>
                  <Tag
                    color={
                      statusCall === EORDER_STATUS_CALL.CANCELLED
                        ? 'error'
                        : 'success'
                    }
                  >
                    <Typography variant="subtitle2">
                      {statusCall === EORDER_STATUS_CALL.CANCELLED
                        ? 'ไม่รับสาย'
                        : 'โทรแล้ว'}
                    </Typography>
                  </Tag>
                  <Button
                    variant="outlined"
                    size="small"
                    fullWidth
                    startIcon={<AccessTimeIcon color={'info'} />}
                    color={'info'}
                    onClick={() =>
                      changeStatusCall(
                        EORDER_STATUS_CALL.NOT_CALLED,
                        order?.objectId!,
                      )
                    }
                  >
                    ยกเลิก
                  </Button>
                </>
              )}
            </Box>
          )
        },
      },
      {
        title: 'หมายเหตุ',
        dataIndex: 'note',
        key: 'note',
        align: 'center' as const,
        render: (note: string) => (
          <Typography variant="subtitle2" textAlign={'center'}>
            {note?.length > 0 ? note : <em>ไม่ระบุ</em>}
          </Typography>
        ),
      },
      {
        title: 'ผู้ดูแล',
        dataIndex: 'assignTelesaleBy',
        key: 'assignTelesaleBy',
        width: 120,
        align: 'center' as const,
        render: (assignTelesaleBy: string) => (
          <Typography variant="subtitle1">{assignTelesaleBy}</Typography>
        ),
      },
      {
        title: 'วันที่เพิ่ม',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 120,
        align: 'center' as const,
        render: (createdAt: string, order: IOrder) => (
          <Grid container flexDirection={'column'} textAlign={'center'}>
            <Grid item>
              <Typography variant="subtitle1">{order?.actor}</Typography>
            </Grid>
            <Grid item>
              <Typography
                sx={{ mb: 0, lineHeight: 1.1 }}
                fontSize={12}
                variant="subtitle2"
                color="text.secondary"
              >
                {dayjs(createdAt).format(ONLY_DATE_TIME_FORMAT)}
              </Typography>
            </Grid>
          </Grid>
        ),
      },
      {
        title: 'วันที่แก้ไข',
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        width: 120,
        align: 'center' as const,
        render: (updatedAt: string, order: IOrder) => {
          const lastAction =
            order.actions?.[order.actions?.length - 1] ??
            ({} as {
              actor: string
              date: string
              createdAt: string
              action: string
            })
          return (
            <Grid container flexDirection={'column'} textAlign={'center'}>
              <Grid item>
                <Typography variant="subtitle1">
                  {lastAction?.actor ?? ''}
                </Typography>
              </Grid>
              <Grid item>
                <Typography
                  variant="subtitle2"
                  fontSize={12}
                  sx={{ mb: 1, lineHeight: 1.1 }}
                  color="text.secondary"
                >
                  {dayjs(lastAction?.date ?? lastAction?.createdAt).format(
                    ONLY_DATE_TIME_FORMAT,
                  )}
                </Typography>
              </Grid>
              <Grid item>
                <Tag color="success">
                  {ACTION_TYPE_TITLES?.[lastAction?.action ?? ''] ??
                    lastAction?.action ??
                    'ไม่ระบุ'}
                </Tag>
              </Grid>
            </Grid>
          )
        },
      },
      {
        dataIndex: '',
        key: 'action',
        width: 60,
        align: 'center' as const,
        render: (_: undefined, order: IOrder) => (
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item>
              <IconButton
                component={Link}
                to={`${PageUrls.Orders.Detail}/${order.objectId}`}
              >
                <InfoIcon />
              </IconButton>
            </Grid>
          </Grid>
        ),
      },
    ]

  const rowSelection = {
    selectedRowKeys: selectedData.map((order: IOrder) => order.objectId),
    onChange: onSelectChange,
  }

  const PaginationToolbar = (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      mb={2}
    >
      <Typography variant="body2">
        {total > 0 ? `${total.toLocaleString()} รายการ · ` : ''}
        หน้า {pageIndex}
      </Typography>
      <Box display="flex" gap={1} alignItems="center">
        <Select
          value={filter.perPage ?? 15}
          onChange={(value) => onPerPageChange?.(value)}
          style={{ width: 90 }}
          options={PER_PAGE_OPTIONS.map((v) => ({
            value: v,
            label: `${v} / หน้า`,
          }))}
        />
        <AntdButton disabled={!hasPrev || isLoading} onClick={onPrev}>
          ก่อนหน้า
        </AntdButton>
        <AntdButton
          type="primary"
          disabled={!hasNext || isLoading}
          onClick={onNext}
        >
          ถัดไป
        </AntdButton>
      </Box>
    </Box>
  )

  return (
    <>
      <Dialog
        open={!!slipUrl}
        onClose={() => setSlipUrl(null)}
        maxWidth="sm"
        PaperProps={{
          sx: {
            position: 'relative',
            m: 2,
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: 'transparent',
            boxShadow: 'none',
          },
        }}
      >
        <IconButton
          size="small"
          onClick={() => setSlipUrl(null)}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            color: '#fff',
            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Box
          component="img"
          src={slipUrl ?? ''}
          alt="สลิปการโอนเงิน"
          sx={{
            display: 'block',
            maxWidth: '100%',
            maxHeight: '85vh',
            objectFit: 'contain',
          }}
        />
      </Dialog>
      <CircularLoading isLoading={isLoading} />
      {PaginationToolbar}
      <Table
        size={'middle'}
        rowSelection={rowSelection}
        columns={columns}
        scroll={{
          x: columns.reduce(
            (pre, cur: any) => (cur.width ? cur.width + pre : pre),
            0,
          ),
        }}
        dataSource={data?.map((order: IOrder, index: number) => {
          return {
            ...order,
            key: order.objectId,
            numberRow:
              index + 1 + (pageIndex - 1) * (filter.perPage || 0),
          }
        })}
        bordered
        pagination={false}
      />
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={2}
      >
        <Typography variant="body2">
          {total > 0 ? `${total.toLocaleString()} รายการ · ` : ''}
          หน้า {pageIndex}
        </Typography>
        <Box display="flex" gap={1} alignItems="center">
          <Select
            value={filter.perPage ?? 15}
            onChange={(value) => onPerPageChange?.(value)}
            style={{ width: 90 }}
            options={PER_PAGE_OPTIONS.map((v) => ({
              value: v,
              label: `${v} / หน้า`,
            }))}
          />
          <AntdButton disabled={!hasPrev || isLoading} onClick={onPrev}>
            ก่อนหน้า
          </AntdButton>
          <AntdButton
            type="primary"
            disabled={!hasNext || isLoading}
            onClick={onNext}
          >
            ถัดไป
          </AntdButton>
        </Box>
      </Box>
    </>
  )
}

export default OrderListTeleSaleTable
